import { useCallback, useEffect, useState } from "react";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Tv } from "lucide-react";
import { toast } from "sonner";

export default function WallSyncPanel({ wallId, version, disabled }) {
  const [report, setReport] = useState(null);
  const [startAt, setStartAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const { data } = await apiAdmin.get(`/video-walls/${wallId}/sync-report`);
      setReport(data);
    } catch { /* report is informational */ }
  }, [wallId]);

  useEffect(() => { load(); }, [load, version]);

  useEffect(() => {
    if (!startAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startAt]);

  const resync = async () => {
    if (!window.confirm("Las pantallas del grupo se pondrán en espera unos 60 segundos y luego arrancarán su lista todas juntas. ¿Continuar?")) return;
    try {
      const { data } = await apiAdmin.post(`/video-walls/${wallId}/resync`);
      setStartAt(new Date(data.start_at).getTime());
      setNow(Date.now());
      toast.success("Sincronización programada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const secsLeft = startAt ? Math.max(0, Math.round((startAt - now) / 1000)) : 0;

  return (
    <div className="space-y-4" data-testid="wall-sync-panel">
      {report && (
        <div className="space-y-1.5">
          {report.tvs.map((t) => (
            <div key={t.tv_id} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`wall-sync-tv-${t.tv_id}`}>
              <Tv className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium text-slate-800 truncate max-w-[160px]">{t.name}</span>
              <span className="text-slate-400">·</span>
              <span>{t.items} en menú{t.items !== t.padded_to ? ` → ${t.padded_to}` : ""}</span>
              {t.videos > 0 && <span className="text-amber-600">· {t.videos} video</span>}
            </div>
          ))}
        </div>
      )}
      {report?.warnings?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1" data-testid="wall-sync-warnings">
          {report.warnings.map((w) => (
            <p key={w} className="flex gap-1.5 text-[11px] leading-relaxed text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{w}
            </p>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={resync} disabled={disabled || secsLeft > 0} data-testid="wall-resync-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${secsLeft > 0 ? "animate-spin" : ""}`} />
          Sincronizar pantallas ahora
        </Button>
        {disabled && <span className="text-xs text-amber-600">Guarda los cambios primero</span>}
        {secsLeft > 0 && <span className="text-xs text-blue-600" data-testid="wall-resync-countdown">Arrancan juntas en {secsLeft}s…</span>}
      </div>
      <p className="text-[11px] leading-relaxed text-slate-500">
        Úsalo una vez después de guardar cambios: todas las TVs empiezan su lista al mismo segundo y el Wall llega junto.
      </p>
    </div>
  );
}
